/* A visual map of the complete existing published feedback cohort. */
if (!customElements.get('wi-cro-review-map')) {
  customElements.define('wi-cro-review-map', class extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this.hidden = true;
      this._controller = new AbortController();
      const options = { signal: this._controller.signal };
      const root = this.closest('[data-wi-reviews]');
      if (!root) return;
      const initialise = () => {
        if (this._initialised || !this.isConnected || this._controller?.signal.aborted) return;
        // Wait for the existing lens to be ready before exposing another control for it.
        if (root.dataset.wiReviewsReady !== 'true') return;
        const grid = root.querySelector('[data-wi-review-grid]');
        const filter = root.querySelector('[data-wi-review-filter]');
        if (!grid || !filter || grid.closest('[data-wi-reviews]') !== root || filter.closest('[data-wi-reviews]') !== root) return;
        const groups = ['all', 'critical', 'positive'];
        const choices = [...groups, 'star-1', 'star-2', 'star-3', 'star-4', 'star-5'];
        if (!choices.every(choice => Array.from(filter.options).some(option => option.value === choice))) return;
        const cards = Array.from(grid.querySelectorAll('[data-wi-review]'));
        if (!cards.length) return;
        const stars = [0, 0, 0, 0, 0];
        const ids = new Set();
        for (const card of cards) {
          const originalRating = String(card.dataset.rating ?? '').trim();
          const id = String(card.dataset.reviewId ?? '').trim();
          if (!/^[1-5](?:\.0+)?$/.test(originalRating) || !id || ids.has(id) || card.closest('[data-wi-reviews]') !== root) return;
          ids.add(id);
          stars[Number(originalRating) - 1] += 1;
        }
        const rows = Array.from(this.querySelectorAll('[data-wi-cro-star]'));
        const buttons = Array.from(this.querySelectorAll('[data-wi-cro-review-filter]'));
        const total = this.querySelector('[data-wi-cro-review-total]');
        if (!total || rows.length !== 5 || new Set(rows.map(row => row.dataset.wiCroStar)).size !== 5 ||
            rows.some(row => !/^[1-5]$/.test(row.dataset.wiCroStar) || !row.querySelector('[data-wi-cro-star-fill]') || !row.querySelector('[data-wi-cro-star-count]') || row.querySelector('[data-wi-cro-star-filter]')?.dataset.wiCroStarFilter !== row.dataset.wiCroStar) ||
            buttons.length !== 3 || !groups.every(group => buttons.some(button => button.dataset.wiCroReviewFilter === group && button.querySelector('[data-wi-cro-bucket-count]')))) return;
        const buckets = { all: cards.length, critical: stars[0] + stars[1] + stars[2], positive: stars[3] + stars[4] };
        total.textContent = `${cards.length} published feedback ${cards.length === 1 ? 'entry' : 'entries'}`;
        rows.forEach(row => {
          const count = stars[Number(row.dataset.wiCroStar) - 1];
          row.querySelector('[data-wi-cro-star-count]').textContent = String(count);
          row.querySelector('[data-wi-cro-star-fill]').style.width = `${count / cards.length * 100}%`;
          const button = row.querySelector('[data-wi-cro-star-filter]');
          const star = row.dataset.wiCroStar;
          button.setAttribute('aria-label', `Filter to ${star} ${star === '1' ? 'star' : 'stars'}, ${count} published feedback ${count === 1 ? 'entry' : 'entries'}`);
          button.addEventListener('click', () => {
            filter.value = `star-${star}`;
            filter.dispatchEvent(new Event('change', { bubbles: true }));
          }, options);
        });
        const sync = () => {
          const selected = choices.includes(filter.value) ? filter.value : 'all';
          buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.wiCroReviewFilter === selected)));
          rows.forEach(row => row.querySelector('[data-wi-cro-star-filter]').setAttribute('aria-pressed', String(selected === `star-${row.dataset.wiCroStar}`)));
        };
        buttons.forEach(button => {
          const group = button.dataset.wiCroReviewFilter;
          button.querySelector('[data-wi-cro-bucket-count]').textContent = `(${buckets[group]})`;
          button.addEventListener('click', () => {
            // Reuse the original select's normal change path: no card/body/summary mutation here.
            filter.value = group;
            filter.dispatchEvent(new Event('change', { bubbles: true }));
            sync();
          }, options);
        });
        filter.addEventListener('change', sync, options);
        root.addEventListener('wi:reviews:updated', sync, options);
        this._initialised = true;
        this._readyObserver?.disconnect();
        sync();
        this.hidden = false;
      };
      this._readyObserver = new MutationObserver(initialise);
      this._readyObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-wi-reviews-ready'] });
      document.addEventListener('DOMContentLoaded', initialise, { ...options, once: true });
      initialise();
    }
    disconnectedCallback() {
      this._readyObserver?.disconnect();
      this._controller?.abort();
      this._readyObserver = null;
      this._controller = null;
      this._initialised = false;
      this.hidden = true;
    }
  });
}
